// THE durable offline queue (ADR-0008, build brief §7). One store, one code path:
// every user-generated write — completion-endpoint operations and photo uploads — is
// recorded in IndexedDB BEFORE any network attempt. The op's id IS its idempotency key
// (minted when the tech acted), so drains are safe to retry by construction.
//
// Outcome rules per attempt:
//   success            -> op deleted; server truth applied
//   409 conflict       -> op dropped ON PURPOSE (definitive answer: someone else won;
//                         the losing attempt is preserved in the SERVER audit log),
//                         toast names the winner, board re-syncs
//   401 / 429 / 5xx / network -> kept, retried with exponential backoff + jitter
//   other 4xx          -> moved to the DEAD-LETTER store and surfaced as
//                         "N need attention" — silent data loss is worse than a
//                         visible error (v1 dropped these silently; v2 never does)
import { computed, signal } from '@preact/signals';
import { ApiError, apiPost, apiUpload, authRequired } from './api';
import { DEAD, OPS, idbAll, idbDelete, idbGet, idbPut } from './idb';
import { toast } from '../state/toast';

export interface QueueOp {
  opId: string; // client-minted UUID; doubles as the Idempotency-Key
  kind: 'completion' | 'photo';
  callId: string;
  label: string; // human description for the attention sheet
  createdAt: number;
  tries: number;
  nextAt: number;
  body?: Record<string, unknown>; // completion-endpoint POST body
  blob?: Blob; // photo bytes (captured BEFORE any upload attempt)
}

export interface DeadOp extends QueueOp {
  failedAt: number;
  reason: string;
}

export const pendingCount = signal(0);
export const deadOps = signal<DeadOp[]>([]);
export const deadCount = computed(() => deadOps.value.length);

/** Called with server truth after a successful completion-endpoint drain. */
type ApplyFn = (map: Record<string, unknown>) => void;
let applyCompletions: ApplyFn = () => {};
let onQueueSettled: () => void = () => {};
export function wireQueue(apply: ApplyFn, settled: () => void): void {
  applyCompletions = apply;
  onQueueSettled = settled;
}

async function recount(): Promise<void> {
  pendingCount.value = (await idbAll<QueueOp>(OPS)).length;
  deadOps.value = (await idbAll<DeadOp>(DEAD)).sort((a, b) => b.failedAt - a.failedAt);
}

/** Durably record an op, then try to send immediately. Resolves once RECORDED. */
export async function enqueue(op: Omit<QueueOp, 'tries' | 'nextAt' | 'createdAt'>): Promise<void> {
  try {
    await idbPut(OPS, { ...op, createdAt: Date.now(), tries: 0, nextAt: 0 });
    await recount();
  } catch {
    // IDB unavailable (rare; private-mode edge cases): not durable — say so loudly.
    toast('Warning: this phone cannot save offline. Stay connected until it syncs.');
  }
  void drain();
}

const backoff = (tries: number): number =>
  Math.min(300_000, 5_000 * 2 ** tries) * (0.75 + Math.random() * 0.5);

async function send(op: QueueOp): Promise<void> {
  if (op.kind === 'photo') {
    await apiUpload<{ key: string }>(
      `/api/photos?id=${encodeURIComponent(op.callId)}`,
      op.blob!,
      op.opId,
    );
  } else {
    const map = await apiPost<Record<string, unknown>>('/api/completions', op.body, {
      idempotencyKey: op.opId,
    });
    applyCompletions(map);
  }
}

async function toDead(op: QueueOp, reason: string): Promise<void> {
  await idbPut(DEAD, { ...op, failedAt: Date.now(), reason } satisfies DeadOp);
  await idbDelete(OPS, op.opId);
  toast('A change could not sync — see "needs attention".');
  onQueueSettled(); // board re-syncs so optimistic state reverts to server truth
}

let draining = false;
let wakeTimer: ReturnType<typeof setTimeout> | undefined;

/** Drain in strict FIFO. Stops on offline, auth-gate, or a not-yet-due head op. */
export async function drain(): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    for (;;) {
      if (!navigator.onLine || authRequired.value) break;
      const ops = (await idbAll<QueueOp>(OPS)).sort((a, b) => a.createdAt - b.createdAt);
      const op = ops[0];
      if (!op) break;
      if (op.nextAt > Date.now()) {
        clearTimeout(wakeTimer);
        wakeTimer = setTimeout(() => void drain(), op.nextAt - Date.now() + 50);
        break;
      }
      try {
        await send(op);
        await idbDelete(OPS, op.opId);
      } catch (e) {
        if (e instanceof ApiError) {
          if (e.status === 401) break; // gate shown; resume after unlock
          if (e.status === 409) {
            const by = (e.body.alreadyBy as string) || 'someone else';
            toast(`Already closed by ${by} — your attempt was noted for the office.`);
            await idbDelete(OPS, op.opId);
            onQueueSettled();
            continue;
          }
          if (e.status !== 429 && e.status >= 400 && e.status < 500) {
            await toDead(op, (e.body.error as string) || `rejected (${e.status})`);
            continue;
          }
        }
        // Transient: count the attempt, back off, stop this pass (FIFO order holds).
        op.tries += 1;
        op.nextAt = Date.now() + backoff(op.tries);
        await idbPut(OPS, op);
        clearTimeout(wakeTimer);
        wakeTimer = setTimeout(() => void drain(), op.nextAt - Date.now() + 50);
        break;
      }
    }
  } finally {
    draining = false;
    await recount().catch(() => {});
  }
}

export async function retryDead(opId: string): Promise<void> {
  const op = await idbGet<DeadOp>(DEAD, opId);
  if (!op) return;
  await idbDelete(DEAD, opId);
  const rest: QueueOp & Partial<DeadOp> = { ...op };
  delete rest.failedAt;
  delete rest.reason;
  await idbPut(OPS, { ...rest, tries: 0, nextAt: 0 });
  await recount();
  void drain();
}

export async function discardDead(opId: string): Promise<void> {
  await idbDelete(DEAD, opId);
  await recount();
  onQueueSettled();
}

/** Boot + wakeup wiring: rehydrate counts, then drain on every trigger §7 names. */
export function initQueue(): void {
  void recount().then(() => void drain());
  window.addEventListener('online', () => void drain());
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void drain();
  });
  setInterval(() => void drain(), 30_000);
}
